import { pluralize } from 'numeralize-ru';
import { FormatTimeObject, FormatTimeParams } from './types';
import {
	addMinutes,
	differenceInMinutes,
	format,
	isToday,
	isTomorrow,
	parse,
} from 'date-fns';
import { Option } from 'shared/types';
import { formatISODate, formatWithLocale } from '../datepicker';

export const checkUtcInTime = (time: string) => {
	const noUtcZRegex = /[+-]\d{2}:\d{2}$/;
	if (noUtcZRegex.test(time)) {
		return true;
	}
	return false;
};

export const formatTime = ({
	time,
	day,
	byUtc = true,
}: FormatTimeParams): FormatTimeObject | false => {
	let date = new Date();
	if (time) {
		date = new Date(time);
	}

	if (time) {
		if (checkUtcInTime(time)) {
			byUtc = false;
		}
	}

	if (isNaN(date.getTime())) {
		return false;
	}

	if (day) {
		date = day;
	}

	let hours = date.getHours();
	let minutes = date.getMinutes();
	let seconds = date.getSeconds();

	if (byUtc) {
		hours = date.getUTCHours();
		minutes = date.getUTCMinutes();
		seconds = date.getUTCSeconds();
	}

	const timeObject: FormatTimeObject = {
		hours: '',
		minutes: '',
		time: '',
		timeWithSec: '',
	};

	let formatTime = '';
	if (hours < 10) {
		formatTime = `0${hours}`;
	} else {
		formatTime = `${hours}`;
	}
	timeObject.hours = formatTime;

	if (minutes < 10) {
		formatTime += `:0${minutes}`;
		timeObject.minutes = `0${minutes}`;
	} else {
		formatTime += `:${minutes}`;
		timeObject.minutes = `${minutes}`;
	}

	timeObject.time = formatTime;
	timeObject.timeWithSec = formatTime;

	if (seconds < 10) {
		timeObject.timeWithSec += `:0${seconds}`;
	} else {
		timeObject.timeWithSec += `:${seconds}`;
	}

	return timeObject;
};

export const getDiffBetweenDates = (timestampOne: Date, timestampTwo: Date) => {
	if (timestampOne && timestampTwo) {
		const timestamp1 = timestampOne.getTime();
		const timestamp2 = timestampTwo.getTime();

		const difference = timestamp2 - timestamp1;

		const minutes = Math.floor(Math.abs(difference / 60000));
		const hours = Math.floor(Math.abs(difference / 3600000));
		const days = Math.floor(Math.abs(difference / (3600000 * 24)));

		return { difference, minutes, hours, days };
	}
};

export const formatDuration = (dateString: string, durationMinutes: number, short: boolean = false) => {
	const date = new Date(dateString);
	const initialMinutes = date.getMinutes();

	date.setMinutes(initialMinutes + durationMinutes);

	const totalMinutes = durationMinutes;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	if (hours === 0) {
		return `${minutes} ${pluralize(minutes, 'мин', 'мин', 'мин')}`;
	}

	const hoursStr = `${hours} ${pluralize(hours, 'ч', 'ч', 'ч')}`;
	const minutesStr = `${minutes} ${pluralize(
		minutes,
		'минута',
		'минуты',
		'минут',
	)}`;

	if (short) {
		return `${hoursStr} ${minutes} ${pluralize(
			minutes,
			'мин', 'мин', 'мин'
		)}`;
	}

	return `${hoursStr} ${minutesStr}`;
};

export const formatDateTimeToISO = (date: string, time: string): string => {
	const [day, month, year] = date.split('.').map(Number);
	const [hours, minutes] = time.split(':').map(Number);
	const dateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes));
	return dateTime.toISOString();
};

export const formatDateToRelativeDate = (isoString: string) => {
	const date = new Date(isoString);

	if (isToday(date)) {
		return 'Сегодня';
	}

	if (isTomorrow(date)) {
		return 'Завтра';
	}

	return formatWithLocale(date, 'dd MMMM yyyy');
};

export const replaceTimeInISODate = (
	isoDate: string,
	newTime: string,
	isUtc: boolean = true,
): string => {
	const [newHours, newMinutes] = newTime.split(':').map(Number);

	if (
		isNaN(newHours) ||
		newHours < 0 ||
		newHours > 23 ||
		isNaN(newMinutes) ||
		newMinutes < 0 ||
		newMinutes > 59
	) {
		throw new Error('Invalid time format');
	}

	const date = new Date(isoDate);
	date.setHours(newHours, newMinutes, 0, 0);
	return formatISODate(date, isUtc);
};

export const timeToMinutes = (time: string): number => {
	const timeParts = time.split(':');
	if (timeParts.length !== 2) {
		throw new Error('Invalid time format. Expected "HH:MM"');
	}

	const [hours, minutes] = timeParts.map(part => parseInt(part, 10));

	if (
		isNaN(hours) ||
		isNaN(minutes) ||
		hours < 0 ||
		minutes < 0 ||
		minutes >= 60
	) {
		throw new Error(
			'Invalid time value. Hours must be >= 0 and minutes must be >= 0 and < 60',
		);
	}

	return hours * 60 + minutes;
};

export const minutesToTime = (minutes: number): string => {
	if (
		typeof minutes !== 'number' ||
		minutes < 0 ||
		!Number.isInteger(minutes)
	) {
		throw new Error(
			'Invalid input. Minutes must be a non-negative integer.',
		);
	}

	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;

	const paddedHours = hours.toString().padStart(2, '0');
	const paddedMinutes = remainingMinutes.toString().padStart(2, '0');

	return `${paddedHours}:${paddedMinutes}`;
};

export const generateDurations = (
	startDuration: string,
	endDuration: string,
	interval: number,
): string[] => {
	const startDate = parse(startDuration, 'HH:mm', new Date(0));
	const endDate = parse(endDuration, 'HH:mm', new Date(0));

	const totalMinutes = differenceInMinutes(endDate, startDate);

	const durations: string[] = [];
	for (let minutes = 0; minutes <= totalMinutes; minutes += interval) {
		const currentDuration = addMinutes(startDate, minutes);
		durations.push(format(currentDuration, 'HH:mm'));
	}

	return durations;
};

export const generateDurationObjects = (
	startDuration: string,
	endDuration: string,
	interval: number,
): Option[] => {
	const durations = generateDurations(startDuration, endDuration, interval);
	return durations.map(duration => ({
		value: duration,
		label: duration,
	}));
};

export const extractDate = (isoString: string) => isoString.split('T');

export const setTimeToDateObject = (time: string, date: Date) => {
	const [hours, minutes] = time.split(':').map(Number);
	date.setHours(hours);
	date.setMinutes(minutes);
	return date;
};
